import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, List, ListItem, ListItemText, Checkbox, IconButton,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { API_BASE_URL } from '../api';

const TeamClientAssignments = ({ teamId, open, onClose }) => {
  const [assignedClients, setAssignedClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [searchAssigned, setSearchAssigned] = useState('');
  const [searchAll, setSearchAll] = useState('');
  const [showAllClients, setShowAllClients] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(true);
  const [loadingAllClients, setLoadingAllClients] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchAssignedClients();
    }
  }, [open, teamId]);

  const fetchAssignedClients = () => {
    setLoadingAssigned(true);
    axios.get(`${API_BASE_URL}/api/teams/${teamId}/assigned-clients/`)
      .then(response => {
        setAssignedClients(response.data || []);
        setLoadingAssigned(false);
      })
      .catch(error => {
        setError('Failed to load assigned clients');
        setLoadingAssigned(false);
      });
  };

  const handleSearchClients = () => {
    setShowAllClients(true);
    setLoadingAllClients(true);
    axios.get(`${API_BASE_URL}/api/client-list/`)
      .then(response => {
        setAllClients(response.data || []);
        setLoadingAllClients(false);
      })
      .catch(error => {
        setError('Failed to load clients');
        setLoadingAllClients(false);
      });
  };

  const filteredAssignedClients = assignedClients.filter(client =>
    (client.display_name || 'Unknown Client').toLowerCase().includes(searchAssigned.toLowerCase())
  );

  const filteredAllClients = allClients.filter(client =>
    (client.primary_contact.display_name || 'Unknown Client').toLowerCase().includes(searchAll.toLowerCase()) &&
    !assignedClients.some(ac => ac.id === client.id)
  );

  const handleToggleClient = (clientId) => {
    setSelectedClients(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleAssignClients = () => {
    axios.post(`${API_BASE_URL}/api/teams/${teamId}/assign-clients/`, {
      client_ids: selectedClients,
      action: 'assign',
    })
      .then(() => {
        fetchAssignedClients();
        setSelectedClients([]);
        setShowAllClients(false);
      })
      .catch(error => {
        setError('Failed to assign clients');
      });
  };

  const handleUnassignClient = (clientId) => {
    axios.post(`${API_BASE_URL}/api/teams/${teamId}/assign-clients/`, {
      client_ids: [clientId],
      action: 'unassign',
    })
      .then(() => {
        fetchAssignedClients();
      })
      .catch(error => {
        setError('Failed to unassign client');
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Team Client Assignments
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        {/* Assigned Clients Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Assigned Clients
          </Typography>
          {loadingAssigned ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <TextField
                label="Search Assigned Clients"
                value={searchAssigned}
                onChange={(e) => setSearchAssigned(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                sx={{ mb: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}
              />
              <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <List>
                  {filteredAssignedClients.length > 0 ? (
                    filteredAssignedClients.map(client => (
                      <ListItem
                        key={client.id}
                        sx={{ py: 0.5, '&:hover': { backgroundColor: '#f5f5f5' } }}
                        secondaryAction={
                          <IconButton edge="end" onClick={() => handleUnassignClient(client.id)}>
                            <RemoveIcon sx={{ color: '#d32f2f' }} />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={client.display_name || 'Unknown Client'} />
                      </ListItem>
                    ))
                  ) : (
                    <Typography sx={{ p: 2, color: '#666', textAlign: 'center' }}>
                      No clients assigned
                    </Typography>
                  )}
                </List>
              </Box>
            </>
          )}
        </Box>

        {/* Search Clients Section */}
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={handleSearchClients}
            sx={{ mb: 2, borderRadius: 1 }}
          >
            Search Clients
          </Button>
          <Dialog open={showAllClients} onClose={() => setShowAllClients(false)} fullWidth maxWidth="sm">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Select Clients to Assign
              </Typography>
              <IconButton onClick={() => setShowAllClients(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {loadingAllClients ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  <TextField
                    label="Search Clients"
                    value={searchAll}
                    onChange={(e) => setSearchAll(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}
                  />
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <List>
                      {filteredAllClients.length > 0 ? (
                        filteredAllClients.map(client => (
                          <ListItem
                            key={client.id}
                            sx={{ py: 0.5, '&:hover': { backgroundColor: '#f5f5f5' } }}
                          >
                            <Checkbox
                              checked={selectedClients.includes(client.id)}
                              onChange={() => handleToggleClient(client.id)}
                              sx={{ mr: 1 }}
                            />
                            <ListItemText primary={client.primary_contact.display_name || 'Unknown Client'} />
                          </ListItem>
                        ))
                      ) : (
                        <Typography sx={{ p: 2, color: '#666', textAlign: 'center' }}>
                          No clients available to assign
                        </Typography>
                      )}
                    </List>
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowAllClients(false)} sx={{ color: '#666' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAssignClients}
                disabled={selectedClients.length === 0}
                sx={{ borderRadius: 1 }}
              >
                Done
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TeamClientAssignments;